import { take, call, put, select } from 'redux-saga/effects'
import { apiSaga, formSaga } from './sagas'
import {
  deletePaymentOption,
  loadPaymentOptions,
  attemptChangePassword,
  callApi
} from '../../api/api'
import { selectCurrentUser } from './selectors'

// ------------------------------------
// Action Types & Creators
// ------------------------------------
export const LOAD_PROFILE = 'foodsby/profile/LOAD_PROFILE'
export const LOAD_PROFILE_SUCCESS = 'foodsby/profile/LOAD_PROFILE_SUCCESS'
export const LOAD_PROFILE_FAILURE = 'foodsby/profile/LOAD_PROFILE_FAILURE'

export const UPDATE_PERSONAL_INFO = 'foodsby/profile/UPDATE_PERSONAL_INFO'
export const UPDATE_PERSONAL_INFO_SUCCESS =
  'foodsby/profile/UPDATE_PERSONAL_INFO_SUCCESS'
export const UPDATE_PERSONAL_INFO_FAILURE =
  'foodsby/profile/UPDATE_PERSONAL_INFO_FAILURE'

export const CHANGE_PERSONAL_INFO_EDITING_MODE =
  'foodsby/profile/CHANGE_PERSONAL_INFO_EDITING_MODE'
export const CHANGE_PASSWORD_EDITING_MODE =
  'foodsby/profile/CHANGE_PASSWORD_EDITING_MODE'

export const CHANGE_PASSWORD = 'foodsby/profile/CHANGE_PASSWORD'
export const CHANGE_PASSWORD_SUCCESS = 'foodsby/profile/CHANGE_PASSWORD_SUCCESS'
export const CHANGE_PASSWORD_FAILURE = 'foodsby/profile/CHANGE_PASSWORD_FAILURE'

export const LOAD_PAYMENT_OPTIONS = 'foodsby/profile/LOAD_PAYMENT_OPTIONS'
export const LOAD_PAYMENT_OPTIONS_SUCCESS =
  'foodsby/profile/LOAD_PAYMENT_OPTIONS_SUCCESS'
export const LOAD_PAYMENT_OPTIONS_FAILURE =
  'foodsby/profile/LOAD_PAYMENT_OPTIONS_FAILURE'

export const DELETE_PAYMENT_OPTION = 'foodsby/profile/DELETE_PAYMENT_OPTION'
export const DELETE_PAYMENT_OPTION_SUCCESS =
  'foodsby/profile/DELETE_PAYMENT_OPTION_SUCCESS'
export const DELETE_PAYMENT_OPTION_FAILURE =
  'foodsby/profile/DELETE_PAYMENT_OPTION_FAILURE'

export const THANK_YOU_MODAL_UPDATE = 'foodsby/profile/THANK_YOU_MODAL_UPDATE'
export const THANK_YOU_MODAL_UPDATE_SUCCESS =
  'foodsby/profile/THANK_YOU_MODAL_UPDATE_SUCCESS'
export const THANK_YOU_MODAL_UPDATE_FAILURE =
  'foodsby/profile/THANK_YOU_MODAL_UPDATE_FAILURE'

export const loadProfileStart = () => {
  return {
    type: LOAD_PROFILE
  }
}

export const loadProfileSuccess = profile => {
  return {
    type: LOAD_PROFILE_SUCCESS,
    payload: { profile }
  }
}

export const loadProfileFailure = error => {
  return {
    type: LOAD_PROFILE_FAILURE,
    error
  }
}

export const updatePersonalInfoStart = personalInfo => {
  return {
    type: UPDATE_PERSONAL_INFO,
    payload: { personalInfo }
  }
}

export const updatePersonalInfoSuccess = profile => {
  return {
    type: UPDATE_PERSONAL_INFO_SUCCESS,
    payload: { profile }
  }
}

export const updatePersonalInfoFailure = error => {
  return {
    type: UPDATE_PERSONAL_INFO_FAILURE,
    error
  }
}

export const changePersonalInfoEditingMode = isEditingPersonalInfo => {
  return {
    type: CHANGE_PERSONAL_INFO_EDITING_MODE,
    payload: { isEditingPersonalInfo }
  }
}

export const changePasswordStart = changePasswordFormData => {
  return {
    type: CHANGE_PASSWORD,
    payload: { changePasswordFormData }
  }
}

export const changePasswordSuccess = response => {
  return {
    type: CHANGE_PASSWORD_SUCCESS,
    payload: { response }
  }
}

export const changePasswordFailure = error => {
  return {
    type: CHANGE_PASSWORD_FAILURE,
    error
  }
}

export const changePasswordEditingMode = isChangingPassword => {
  return {
    type: CHANGE_PASSWORD_EDITING_MODE,
    payload: { isChangingPassword }
  }
}

export const loadPaymentOptionsStart = () => {
  return {
    type: LOAD_PAYMENT_OPTIONS
  }
}

export const loadPaymentOptionsSuccess = paymentOptions => {
  return {
    type: LOAD_PAYMENT_OPTIONS_SUCCESS,
    payload: { paymentOptions }
  }
}

export const loadPaymentOptionsFailure = error => {
  return {
    type: LOAD_PAYMENT_OPTIONS_FAILURE,
    error
  }
}

export const deletePaymentOptionStart = ccProfileId => {
  return {
    type: DELETE_PAYMENT_OPTION,
    payload: { ccProfileId }
  }
}

export const deletePaymentOptionSuccess = () => {
  return {
    type: DELETE_PAYMENT_OPTION_SUCCESS
  }
}

export const deletePaymentOptionFailure = error => {
  return {
    type: DELETE_PAYMENT_OPTION_FAILURE,
    error
  }
}

export const updateModalStart = message => {
  return {
    type: THANK_YOU_MODAL_UPDATE,
    payload: { message }
  }
}

export const updateModalSuccess = () => {
  return {
    type: THANK_YOU_MODAL_UPDATE_SUCCESS
  }
}

export const updateModalFailure = error => {
  return {
    type: THANK_YOU_MODAL_UPDATE_FAILURE,
    error
  }
}

// ------------------------------------
// Action Handlers
// ------------------------------------

const ACTION_HANDLERS = {
  [LOAD_PROFILE]: (state, action) => {
    return {
      ...state,
      isLoadingProfile: true
    }
  },
  [LOAD_PROFILE_SUCCESS]: (state, action) => {
    const {
      payload: { profile }
    } = action
    return {
      ...state,
      isLoadingProfile: false,
      error: undefined,
      profile
    }
  },
  [LOAD_PROFILE_FAILURE]: (state, action) => {
    const { error } = action
    return {
      ...state,
      isLoadingProfile: false,
      error
    }
  },
  [CHANGE_PERSONAL_INFO_EDITING_MODE]: (state, action) => {
    const {
      payload: { isEditingPersonalInfo }
    } = action
    return {
      ...state,
      isEditingPersonalInfo,
      showSuccess: undefined
    }
  },
  [CHANGE_PASSWORD_EDITING_MODE]: (state, action) => {
    const {
      payload: { isChangingPassword }
    } = action
    return {
      ...state,
      isChangingPassword,
      showSuccess: undefined,
      changePasswordFailed: undefined
    }
  },
  [CHANGE_PASSWORD]: state => {
    return {
      ...state,
      submitting: true,
      showSuccess: false,
      changePasswordFailed: false
    }
  },
  [CHANGE_PASSWORD_SUCCESS]: (state, action) => {
    return {
      ...state,
      response: action.payload.response,
      submitting: undefined,
      showSuccess: true,
      isChangingPassword: false,
      changePasswordFailed: false
    }
  },
  [CHANGE_PASSWORD_FAILURE]: (state, action) => {
    return {
      ...state,
      changePasswordFailed: action.error,
      submitting: undefined,
      showSuccess: false,
      isChangingPassword: true
    }
  },
  [UPDATE_PERSONAL_INFO]: (state, action) => {
    const {
      payload: { personalInfo }
    } = action
    const previousProfile = state.profile
    const profile = Object.assign({}, previousProfile, { ...personalInfo })
    return {
      ...state,
      profile,
      previousProfile,
      showSuccess: undefined,
      isUpdatingProfile: true
    }
  },
  [UPDATE_PERSONAL_INFO_SUCCESS]: (state, action) => {
    const {
      payload: { personalInfo }
    } = action
    const previousProfile = state.profile
    const profile = Object.assign({}, previousProfile, { ...personalInfo })
    return {
      ...state,
      profile,
      isUpdatingProfile: false,
      isEditingPersonalInfo: false,
      previousProfile: undefined
    }
  },
  [UPDATE_PERSONAL_INFO_FAILURE]: (state, action) => {
    const { error } = action
    const profile = state.previousProfile
    return {
      ...state,
      profile,
      isUpdatingProfile: false,
      previousProfile: undefined,
      error
    }
  },
  [LOAD_PAYMENT_OPTIONS]: state => {
    return {
      ...state,
      isLoadingPaymentOptions: true
    }
  },
  [LOAD_PAYMENT_OPTIONS_SUCCESS]: (state, action) => {
    const {
      payload: { paymentOptions }
    } = action
    return {
      ...state,
      isLoadingPaymentOptions: false,
      error: undefined,
      paymentOptions
    }
  },
  [LOAD_PAYMENT_OPTIONS_FAILURE]: (state, action) => {
    const { error } = action
    return {
      ...state,
      isLoadingPaymentOptions: false,
      error
    }
  },
  [DELETE_PAYMENT_OPTION]: (state, action) => {
    const {
      payload: { ccProfileId }
    } = action
    const previousPaymentOptions = state.paymentOptions
    const paymentOptions = previousPaymentOptions.filter(
      option => option.ccProfileId !== ccProfileId
    )
    return {
      ...state,
      isDeletingPaymentOption: true,
      previousPaymentOptions,
      paymentOptions
    }
  },
  [DELETE_PAYMENT_OPTION_SUCCESS]: state => {
    return {
      ...state,
      isDeletingPaymentOption: false,
      error: undefined,
      previousPaymentOptions: undefined
    }
  },
  [DELETE_PAYMENT_OPTION_FAILURE]: (state, action) => {
    const { error } = action
    const paymentOptions = state.previousPaymentOptions
    return {
      ...state,
      isDeletingPaymentOption: false,
      previousPaymentOptions: undefined,
      error,
      paymentOptions
    }
  },
  [THANK_YOU_MODAL_UPDATE]: state => {
    return {
      ...state,
      error: undefined,
      updatingModal: true
    }
  },
  [THANK_YOU_MODAL_UPDATE_SUCCESS]: (state, action) => {
    return {
      ...state,
      error: undefined,
      updatingModal: false
    }
  },
  [THANK_YOU_MODAL_UPDATE_FAILURE]: (state, action) => {
    return {
      ...state,
      error: action.error,
      updatingModal: false
    }
  }
}

// ------------------------------------
// Reducer
// ------------------------------------
const initialState = {
  profile: null,
  isLoadingProfile: false,
  token: false,
  showSuccess: undefined,
  tokenValid: true,
  submitting: undefined,
  updatingModal: undefined
}

export default function profile(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}

// ------------------------------------
// Sagas
// ------------------------------------

export function* watchLoadProfile() {
  while (true) {
    yield take(LOAD_PROFILE)
    const user = yield select(selectCurrentUser)

    if (user && user.userId) {
      yield call(
        apiSaga,
        loadProfileByUserId,
        [user.userId],
        loadProfileSuccess,
        loadProfileFailure
      )
    } else {
      yield put(loadProfileFailure())
    }
  }
}

export function* watchUpdatePersonalInfo() {
  while (true) {
    const {
      payload: { personalInfo }
    } = yield take(UPDATE_PERSONAL_INFO)
    const { userId } = yield select(selectCurrentUser)
    try {
      yield call(formSaga, 'personalInfo', patchUserProfile, [
        userId,
        personalInfo
      ])
      yield put(updatePersonalInfoSuccess({ ...personalInfo, userId }))
    } catch (ex) {
      yield put(updatePersonalInfoFailure(ex))
    }
  }
}

export function* watchSubmitChangePassword() {
  while (true) {
    const {
      payload: { changePasswordFormData }
    } = yield take(CHANGE_PASSWORD)

    yield call(
      formSaga,
      'changePassword',
      attemptChangePassword,
      [changePasswordFormData],
      changePasswordSuccess,
      changePasswordFailure
    )
  }
}

export function* watchLoadPaymentOptions() {
  while (true) {
    yield take(LOAD_PAYMENT_OPTIONS)
    yield call(
      apiSaga,
      loadPaymentOptions,
      [],
      loadPaymentOptionsSuccess,
      loadPaymentOptionsFailure
    )
  }
}

export function* watchDeletePaymentOption() {
  while (true) {
    const {
      payload: { ccProfileId }
    } = yield take(DELETE_PAYMENT_OPTION)
    yield call(
      apiSaga,
      deletePaymentOption,
      [ccProfileId],
      deletePaymentOptionSuccess,
      deletePaymentOptionFailure
    )
  }
}

export function* watchUpdateFirstOrderModal() {
  while (true) {
    const {
      payload: { message }
    } = yield take(THANK_YOU_MODAL_UPDATE)
    const { userId } = yield select(selectCurrentUser)
    yield call(
      apiSaga,
      patchUserProfile,
      [userId, message],
      updateModalSuccess,
      updateModalFailure
    )
    yield put(loadProfileStart())
  }
}

// ------------------------------------
// APIs
// ------------------------------------

export const loadProfileByUserId = id =>
  callApi.get(`/api/v1/uaa/users/${id}/profiles`)

export const patchUserProfile = (id, personalInfo) =>
  callApi.put(`/api/v1/uaa/users/${id}/profiles`, personalInfo)
